import helpIcon from '@disy/cadenza-icons/help.svg';
import infoIcon from '@disy/cadenza-icons/info-small.svg';

import { createIconButton } from 'ui/button/button';
import type {
  PopupContent
} from 'ui/popup/popup';
import {
  closePopup,
  createPopup,
  getPopupContent,
  openPopup
} from 'ui/popup/popup';

import { assert } from 'cadenza/utils/custom-error';
import { getTabbables } from 'cadenza/utils/event-util';
import { escapeAndMarkHTML } from 'cadenza/utils/escape-and-mark';
import type { Icon } from 'cadenza/utils/icon/icon';
import { icon as createIcon } from 'cadenza/utils/icon/icon';
import type { Placement } from 'cadenza/utils/position';

import i18n from './toggletip.properties';
import './toggletip.css';

const COMPONENT_NAME = 'd-toggletip';

interface ToggletipOptions {
  toggletip?: string;
  toggletipContent?: PopupContent;
  toggletipPlacement?: Placement;
  toggletipStyleClass?: string;
  icon?: Icon;
  size?: 's' | 'xs';
  ariaLabel?: string;
  styleClass?: string;
}

/**
 * A button with the _only_ purpose to reveal additional information on click in a tooltip.
 *
 * @param [options] - options
 * @param [options.toggletip] - Text content of the tooltip. Can contain limited Markdown-like
 *   formatting. See the supported tags in the the `mark()` function's documentation.
 * @param [options.toggletipContent] - High-fidelity toggletip content (which still must not be interactive!)
 *   If there is `toggletipContent`, the `toggletip` option is ignored.
 * @param [options.toggletipPlacement] - The placement of the tooltip.
 * @param [options.toggletipStyleClass] - A optional CSS class for the tooltip
 * @param [options.icon] - button icon
 * @param [options.size='s'] - button size
 * @param [options.ariaLabel] - button label
 * @param [options.styleClass] - Name of CSS class(es) to add to the component
 * @return The toggletip button
 */
export function createToggletip ({
  toggletip,
  toggletipContent,
  toggletipPlacement,
  toggletipStyleClass,
  icon = createIcon(infoIcon),
  size = 's',
  ariaLabel = i18n('label'),
  styleClass
}: ToggletipOptions = {}) {
  const button = createIconButton(icon, ariaLabel, {
    variant: 'borderless',
    size,
    styleClass: `${COMPONENT_NAME}--button`,
    onpointerdown: (event: Event) => event.stopPropagation(), // prevent close
    onclick: event => onClick(event, toggletipContent, toggletipStyleClass),
    onkeydown
  });
  button.removeAttribute('title'); // There should not be a tooltip on a toggletip.
  button.setAttribute('aria-label', ariaLabel);
  if (toggletip) {
    button.dataset.toggletip = toggletip;
  }
  if (toggletipPlacement) {
    button.dataset.toggletipPlacement = toggletipPlacement;
  }
  if (styleClass) {
    button.classList.add(styleClass);
  }
  return button;
}

function onClick (event: Event, toggletipContent?: PopupContent, toggletipStyleClass?: string) {
  event.stopImmediatePropagation(); // prevent close

  const target = event.currentTarget as HTMLElement;
  const isOpen = target.getAttribute('aria-controls');
  if (!isOpen) {
    const popup = createPopup({ role: 'status', styleClass: COMPONENT_NAME });

    if (toggletipStyleClass) {
      popup.classList.add(toggletipStyleClass);
    }

    // the popup needs to be focusable so that
    // when it's clicked, it's assigned to the relatedTarget property of the button's blur event
    popup.tabIndex = 0;

    const onblur = (blurEvent: FocusEvent) => onButtonOrTooltipBlur(blurEvent, target, popup);
    target.onblur = onblur;
    popup.onblur = onblur;

    openPopup(target, popup, {
      arrow: true,
      placement: target.dataset.toggletipPlacement as Placement || 'right-start'
    });

    // Add the content to the live region in a timeout for screen readers to get the update.
    setTimeout(() => {
      if (toggletipContent) {
        popup.append(...getPopupContent(toggletipContent));
        assert(!getTabbables(popup).length, 'A toggletip must not have interactive content. You may use a popup instead.');
      } else {
        popup.innerHTML = escapeAndMarkHTML(target.dataset.toggletip);
      }
    });

    target.setAttribute('aria-controls', popup.id);
    target.setAttribute('aria-expanded', 'true');
  } else {
    closePopup();
    // We need to remove the onblur handler from the button, otherwise it would immediately close other popup that we open.
    target.onblur = null;
  }
}

function onkeydown (event: KeyboardEvent) {
  if (event.key === 'Escape') {
    const target = event.target as Element;
    const isOpen = target.getAttribute('aria-controls');
    if (isOpen) {
      // prevent closing ancestor dialogs (both are needed)
      event.stopPropagation();
      event.preventDefault();
      closePopup();
    }
  }
}

function onButtonOrTooltipBlur (event: FocusEvent, button: HTMLElement, tooltip: HTMLElement) {
  // close the popup unless the focus goes to the tooltip or the button
  if (event.relatedTarget !== button && event.relatedTarget !== tooltip) {
    closePopup();
    button.onblur = null;
  }
}

/** For usage in JS it is recommended to use the `createToggletip()` function, not this component. */
class Toggletip extends HTMLElement {

  button;

  constructor ({
    toggletip,
    icon = createIcon(infoIcon),
    ariaLabel = i18n('label'),
    styleClass
  }: ToggletipOptions = {}) {
    super();
    this.button = createToggletip({ toggletip, icon, ariaLabel, styleClass });
  }

  connectedCallback () {
    if (!this.button.dataset.toggletip) {
      this.button.dataset.toggletip = this.getAttribute('toggletip') as string;
    }
    this.replaceWith(this.button);
  }

}

customElements.define(COMPONENT_NAME, Toggletip);

export function createHelpToggletip (options: ToggletipOptions) {
  return createToggletip({ ...options, icon: createIcon(helpIcon), size: 'xs' });
}
